// JOBE-0116: ProjectEntitiesPage — top-level route component for
// `/projects/{urn}/entities/{kind}` (Screen C, the project-scoped
// entity browser sub-route).
//
// Composition only: breadcrumb + <EntityBrowser> pinned via
// `defaultChips` to `type=<kind>` and anchored on
// `relations.inProject=<urn>`. Reuses `useProjectDetail` (for the
// breadcrumb's project-name segment + Cedar-denied short-circuit)
// and `useRoster` (for the chip composer's PeopleValuePopover) —
// both extracted from / shared with JOBE-0106's ProjectHomePage.
//
// Spec: specs/in-progress/JOBE-0116-project-entities-subroute.md
// ADR-0023 rollout plan, Phase 6.

import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useProjectDetail } from './useProjectDetail.js';
import { useRoster } from './useRoster.js';
import { EntityBrowser } from '../entitybrowser/EntityBrowser.jsx';
import { EntityTypesProvider } from '../library/EntityTypesProvider.jsx';
import { ProjectKindsRail } from './ProjectKindsRail.jsx';

import './projects.css';

function Loading() {
  return (
    <div className="project-entities-page project-entities-loading" data-testid="project-entities-loading">
      <div className="project-home-skeleton-header" />
      <p className="muted">Loading…</p>
    </div>
  );
}

function CedarDenied() {
  return (
    <div className="project-entities-page project-entities-denied" data-testid="project-entities-denied">
      <div className="card project-home-denied-card">
        <p>You don&apos;t have access to this project.</p>
        <p>
          <a href="/projects" className="link">← Back to projects</a>
        </p>
      </div>
    </div>
  );
}

function ErrorBanner({ error, onRetry }) {
  return (
    <div className="project-entities-page project-entities-error" data-testid="project-entities-error">
      <div className="card project-home-error-card">
        <p>
          Couldn&apos;t load project
          {error?.status ? ` (status ${error.status})` : ''}.
        </p>
        <button
          type="button"
          className="project-home-retry"
          onClick={onRetry}
          data-testid="project-entities-retry"
        >
          Retry
        </button>
      </div>
    </div>
  );
}

export function ProjectEntitiesPage() {
  const { urn: rawUrn, kind } = useParams();
  const projectURN = useMemo(() => {
    if (!rawUrn) return '';
    try { return decodeURIComponent(rawUrn); } catch { return rawUrn; }
  }, [rawUrn]);

  const { project, loadState, error, retry } = useProjectDetail(projectURN);
  const roster = useRoster();
  const defaultChips = useMemo(() => ([{
    kind: 'predicate',
    friendlyToken: 'type',
    wirePredicate: 'type',
    values: [kind],
  }]), [kind]);

  if (loadState === 'loading') return <Loading />;
  if (loadState === 'cedar-denied') return <CedarDenied />;
  if (loadState === 'error') return <ErrorBanner error={error} onRetry={retry} />;

  const projectName = project?.name || projectURN;
  // JOBE-0103 ships `counts.entities_by_kind` under `?expand=rollups`.
  // The hook already requests rollups, so the map is present on the
  // happy path. Fall back to an empty map defensively — the rail
  // renders every registered kind as zero-count in that case.
  const kindCounts = project?.counts?.entities_by_kind || {};

  return (
    // JOBE-0124: EntityTypesProvider hosts the single
    // /api/entity-types fetch the kinds rail consumes via context.
    // Mounted at the page level (mirror of LibraryListPage) so the
    // rail re-renders on /:kind path changes without re-fetching.
    <EntityTypesProvider>
      <div className="project-entities-page" data-testid="project-entities-page">
        <nav className="breadcrumb project-entities-breadcrumb" aria-label="Breadcrumb">
          <Link to="/projects" className="link">Projects</Link>
          <span aria-hidden="true"> / </span>
          <Link to={`/projects/${encodeURIComponent(projectURN)}`} className="link">
            {projectName}
          </Link>
          <span aria-hidden="true"> / </span>
          <span>
            Entities — <span className="mono" data-testid="project-entities-kind-label">{kind}</span>
          </span>
        </nav>
        <div className="project-entities-layout">
          <aside className="project-entities-rail-col">
            <ProjectKindsRail
              projectURN={projectURN}
              activeKind={kind}
              kindCounts={kindCounts}
            />
          </aside>
          <div className="project-entities-body-col">
            <EntityBrowser
              anchor={{ inProject: projectURN }}
              urlStateNamespace="projectEntities"
              defaultChips={defaultChips}
              roster={roster}
            />
          </div>
        </div>
      </div>
    </EntityTypesProvider>
  );
}
